import { Box, CircularProgress } from '@mui/material';
import React, {
  Suspense, useCallback, useMemo, useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { FederatedAdoModal } from '../../../components/krDetails/FederatedAdoModal';
import { Notification, ViewKpiSidePanel } from '../remote-modules';
import { useOpenKpiUpdatesDialog } from '../providers';
import { KPI_LOCATION_VALUES, type KPILocationValue } from '../constants';
import { getSharedData } from '../utils';

const AdoModalLoader = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '400px',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

type ViewKpiSidePanelWrapperProps = {
  open: boolean;
  kpiId: string;
  location: KPILocationValue | null;
  onClose: () => void;
};

export const ViewKpiSidePanelWrapper: React.FC<ViewKpiSidePanelWrapperProps> = ({
  open, kpiId, location, onClose
}) => {
  const { t } = useTranslation();

  const openKpiUpdatesDialog = useOpenKpiUpdatesDialog();

  const [showNotification, setShowNotification] = useState(false);

  const sharedData = useMemo(() => getSharedData(location), [location]);

  const handleSubmitUpdates = useCallback(() => {
    setShowNotification(true);
  }, []);

  const onUpdate = useCallback(() => {
    openKpiUpdatesDialog({
      id: Number(kpiId),
      location: KPI_LOCATION_VALUES.VIEW_KPI_UPDATE,
      onSubmit: handleSubmitUpdates,
    });
  }, [kpiId, handleSubmitUpdates, openKpiUpdatesDialog]);

  const adoFormOverride = useMemo(
    () =>
      (window.wbConfig?.isEnhancedADOKrrUIEnabled
        ? ({
          objectId, relationshipId, onSave, onClose: onAdoClose, objectType
        }) => (
          <Suspense fallback={<AdoModalLoader />}>
            <FederatedAdoModal
              open
              objectId={String(objectId)}
              relationshipId={relationshipId != null ? String(relationshipId) : undefined}
              onSave={onSave}
              onClose={onAdoClose}
              objectType={objectType}
            />
          </Suspense>
        )
        : undefined),
    [window.wbConfig?.isEnhancedADOKrrUIEnabled],
  );

  const externalData = useMemo(
    () => ({
      kpiId,
      onUpdate,
      onClose,
      adoFormOverride,
      ...sharedData,
    }),
    [kpiId, onClose, onUpdate, adoFormOverride, sharedData],
  );

  return (
    <>
      {open && <ViewKpiSidePanel {...externalData} />}
      {showNotification && (
        <Notification
          open={showNotification}
          message={t('KPI data has been updated successfully')}
          type="success"
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};
